import { createContext, useContext, useEffect, useRef } from 'react';
import type { WorkspaceHeaderBreadcrumb } from '@/components/molecules/classroom/WorkspaceHeader';

export interface WorkspaceBreadcrumbValue {
  breadcrumb: WorkspaceHeaderBreadcrumb | null;
}

// Split read/write contexts so publishing a breadcrumb doesn't re-render
// every consumer of the value, and so the setter has a stable identity that
// won't retrigger useSetWorkspaceBreadcrumb's effect.
export const ValueContext = createContext<WorkspaceBreadcrumbValue | null>(null);
export const SetterContext = createContext<((v: WorkspaceBreadcrumbValue | null) => void) | null>(null);

export function useWorkspaceBreadcrumb(): WorkspaceBreadcrumbValue | null {
  return useContext(ValueContext);
}

/**
 * Publishes the current route's breadcrumb to the unified workspace header.
 * Clears on unmount so stale data doesn't persist between routes.
 *
 * Tracks breadcrumb fields by primitive identity to avoid an infinite update
 * loop when callers pass a fresh object literal each render.
 */
export function useSetWorkspaceBreadcrumb(value: WorkspaceBreadcrumbValue | null) {
  const setValue = useContext(SetterContext);
  const subject = value?.breadcrumb?.subject ?? null;
  const subjectHref = value?.breadcrumb?.subjectHref ?? null;
  const chapter = value?.breadcrumb?.chapter ?? null;
  const lesson = value?.breadcrumb?.lesson ?? null;
  // Step 41.B.C4: track sibling-lesson navigation primitives so changing the
  // current lesson re-publishes the prev/next arrows on the breadcrumb-rail.
  const prevLessonHref = value?.breadcrumb?.prevLessonHref ?? null;
  const prevLessonTitle = value?.breadcrumb?.prevLessonTitle ?? null;
  const nextLessonHref = value?.breadcrumb?.nextLessonHref ?? null;
  const nextLessonTitle = value?.breadcrumb?.nextLessonTitle ?? null;

  // Latest snapshot in a ref so the effect can read fresh values without
  // listing the parent object in deps.
  const latestRef = useRef(value);
  latestRef.current = value;

  useEffect(() => {
    if (!setValue) return;
    setValue(latestRef.current);
    return () => setValue(null);
  }, [
    setValue,
    subject,
    subjectHref,
    chapter,
    lesson,
    prevLessonHref,
    prevLessonTitle,
    nextLessonHref,
    nextLessonTitle,
  ]);
}
